import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as practiceUtils from "practiceUtils";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";
import SearchField from "ui/SearchField";
import Check from "views/Check";

interface Props {
  featuredLanguages?: string[];
  languageType: "learning" | "first";
  searchOnlyMode?: boolean;
  /**
   * When provided, only languages whose code passes this test are offered,
   * both in the featured list and in search results.
   */
  languageFilter?: (languageCode: string) => boolean;
  /** The function to call when the language is selected */
  setLanguage: (languageCode: string) => void;
  /** The language chosen already, ticked */
  currentLanguage?: string;
}

interface State {
  /** The search text */
  search: string;
  /** The currently selected result */
  selectedSearchResultIndex: number;
}

interface Language {
  code: string;
  english: string;
  /** In the language itself */
  name: string;
  firstLanguageOnly?: boolean;
}

/** How well a language's names start with what's searched for */
const startScore = (language: Language, search: string) =>
  (language.english.toLowerCase().startsWith(search) ? 10 : 0) +
  (language.name.toLowerCase().startsWith(search) ? 1 : 0);

/**
 * The languages to choose from, as a menu's items (a tick on the one chosen),
 * under a search field. The `featuredLanguages` come first, in a section of
 * their own.
 */
export default class LanguageSelectorView extends preact.Component<
  Props,
  State
> {
  private inputRef = preact.createRef<HTMLInputElement>();

  constructor(props: Props) {
    super(props);
    this.state = {
      search: "",
      selectedSearchResultIndex: 0,
    };
  }

  componentDidMount() {
    if (!deviceDetect.isSmall()) {
      // Defer so the input is laid out (e.g. inside a fading-in modal) before focusing.
      setTimeout(() => this.setFocus(), 100);
    }
  }

  setFocus() {
    this.inputRef.current?.focus();
  }

  private renderLanguage(language: Language, highlighted: boolean) {
    const { search } = this.state;
    const displayString = `${language.english} (${language.name})`;
    // Highlight the search text!
    const highlightIndex = search
      ? displayString.toLowerCase().indexOf(search.trim().toLowerCase())
      : -1;
    const highlightEnd = highlightIndex + search.trim().length;
    const checked = language.code === this.props.currentLanguage;
    return (
      <button
        type="button"
        key={language.code}
        class={cn({ menuItem: true, highlighted })}
        aria-current={checked || undefined}
        onClick={(event) => {
          event.stopPropagation();
          this.props.setLanguage(language.code);
        }}
      >
        <span class="menuCheck">{checked ? <Check /> : null}</span>
        <span class="menuLabel">
          {highlightIndex < 0 ? (
            displayString
          ) : (
            <>
              {displayString.slice(0, highlightIndex)}
              <strong>
                {displayString.slice(highlightIndex, highlightEnd)}
              </strong>
              {displayString.slice(highlightEnd)}
            </>
          )}
        </span>
      </button>
    );
  }

  render() {
    const { languageType, setLanguage, searchOnlyMode, languageFilter } =
      this.props;
    const { search, selectedSearchResultIndex } = this.state;
    const featuredCodes = (this.props.featuredLanguages ?? []).filter(
      (code) => !languageFilter || languageFilter(code)
    );

    const languages = (languageMap.list as Language[])
      .filter(
        (language) =>
          !(languageType === "learning" && language.firstLanguageOnly) &&
          (!languageFilter || languageFilter(language.code))
      )
      .sort((a, b) => a.english.localeCompare(b.english));
    const featured = languages.filter((language) =>
      featuredCodes.includes(language.code)
    );
    const others = languages.filter(
      (language) => !featuredCodes.includes(language.code)
    );

    const searchNormalized = practiceUtils.normalize(search.trim());
    // Searching, one list of the best matches; otherwise the featured
    // languages, then the rest
    const sections = searchNormalized
      ? [
          [...featured, ...others]
            .filter(
              (language) =>
                practiceUtils
                  .normalize(language.english)
                  .includes(searchNormalized) ||
                practiceUtils
                  .normalize(language.name)
                  .includes(searchNormalized)
            )
            // Feature languages which start with the search term first
            .sort(
              (a, b) =>
                startScore(b, searchNormalized) -
                startScore(a, searchNormalized)
            )
            .slice(0, 10),
        ]
      : [featured, others];
    const results = searchNormalized ? sections[0] : [];

    return (
      <div
        class={cn({
          languageSelectorView: true,
          searchOnlyMode,
          browseAndSearchMode: !searchOnlyMode,
        })}
      >
        <SearchField
          inputRef={this.inputRef}
          value={search}
          query={search}
          placeholder="Search for a language"
          onInput={(value) =>
            this.setState({ search: value, selectedSearchResultIndex: 0 })
          }
          onSearch={() => {}}
          onKeyDown={(event) => {
            // On Enter, choose the result picked
            if (event.key === "Enter" && results.length > 0) {
              event.preventDefault();
              setLanguage(results[selectedSearchResultIndex].code);
            }
            if (event.key === "ArrowDown") {
              this.setState({
                selectedSearchResultIndex: Math.min(
                  selectedSearchResultIndex + 1,
                  results.length - 1
                ),
              });
              event.preventDefault();
            }
            if (event.key === "ArrowUp") {
              this.setState({
                selectedSearchResultIndex: Math.max(
                  selectedSearchResultIndex - 1,
                  0
                ),
              });
              event.preventDefault();
            }
          }}
        />
        {!searchOnlyMode || search ? (
          <div class="languageList">
            {sections
              .filter((section) => section.length > 0)
              .map((section, sectionIndex) => (
                <div class="menuSection" key={sectionIndex}>
                  {section.map((language, index) =>
                    this.renderLanguage(
                      language,
                      results.length > 0 && index === selectedSearchResultIndex
                    )
                  )}
                </div>
              ))}
          </div>
        ) : null}
      </div>
    );
  }
}
